import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import firebase, { db } from './firebase';
import { AuthContext } from '../services/contexts/AuthContext';
import {
  GAME, SPINS, BONUS, DRAWS, INVITES, ADMIN_EMAIL, bgDay, phaseAt, summarize, pickWinner,
  cryptoRandomBelow, dateLabel, START_DAY, END_DAY, DRAW_DAY, ANNOUNCE_DAY,
} from '../utils/christmasGame';
import '../styles/christmas-game.css';

const DRAW_DOC = 'result';

const shortDate = day => dateLabel(day, { day: 'numeric', month: 'short' });
const docs = snap => snap.docs.map(d => ({ id: d.id, ...d.data() }));

/** Активна ли е играта – първото нещо, което администраторът иска да знае. */
function GameStatus() {
  const phase = phaseAt(Date.now());
  const [state, text] = !GAME.published
    ? ['off', 'Скрита – вижда я само администраторът. Посетителите получават „страницата не е намерена“ и данни не се събират.']
    : {
        hidden: ['off', `Публикувана, но още не е обявена – банерът и линкът в менюто се появяват на ${dateLabel(ANNOUNCE_DAY)}.`],
        soon: ['soon', `Обявена – колелото тръгва на ${dateLabel(START_DAY)}. Дотогава хората могат само да се регистрират.`],
        live: ['live', `Активна – колелото се върти до ${dateLabel(END_DAY)} включително и завъртанията се записват.`],
        ended: ['soon', `Завъртанията приключиха – тегленето е на ${dateLabel(DRAW_DAY)}.`],
      }[phase];
  return (
    <p className={`xg-game-status xg-game-status-${state}`}>
      <strong>Статус на играта:</strong> {text}
    </p>
  );
}

const Winner = ({ winner }) => (
  <p>
    <strong>{winner.name}</strong> ({winner.email})
    <small> – билет №{winner.ticketNumber} от {winner.totalTickets}, участникът има {winner.tickets}</small>
  </p>
);

export const ChristmasGameAdmin = () => {
  const { user, authLoading } = useContext(AuthContext);
  const isAdmin = user?.email === ADMIN_EMAIL;

  const [spins, setSpins] = useState(null);
  const [bonuses, setBonuses] = useState([]);
  const [invites, setInvites] = useState([]);
  const [saved, setSaved] = useState(null);
  const [trial, setTrial] = useState(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bonusEmail, setBonusEmail] = useState('');
  const [bonusNote, setBonusNote] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      db.collection(SPINS).get(),
      db.collection(BONUS).get(),
      db.collection(DRAWS).doc(DRAW_DOC).get(),
      db.collection(INVITES).get(),
    ])
      .then(([spinSnap, bonusSnap, drawSnap, inviteSnap]) => {
        setSpins(docs(spinSnap));
        setBonuses(docs(bonusSnap));
        setInvites(docs(inviteSnap));
        setSaved(drawSnap.exists ? drawSnap.data() : false);
      })
      .catch(e => setError(e.code === 'permission-denied'
        ? 'Базата още не приема данни за играта – правилата ѝ не са качени. Това е нормално, докато играта не е пусната.'
        : 'Данните не се заредиха. Опресни страницата.'));
  }, [isAdmin]);

  if (authLoading) return <div className="xg-page"><p>Зареждане…</p></div>;
  if (!isAdmin) {
    return (
      <div className="xg-page">
        <p>Тази страница е само за администратора. <Link to="/login">Вход</Link></p>
      </div>
    );
  }
  if (!spins) {
    return (
      <div className="xg-page xg-admin">
        <h1>Коледно колело – админ</h1>
        <GameStatus />
        {error ? <p className="xg-error">{error}</p> : <p>Зареждане…</p>}
      </div>
    );
  }

  const today = bgDay(Date.now());
  const rows = summarize(spins, bonuses, invites);
  const totalTickets = rows.reduce((s, r) => s + r.tickets, 0);
  const codes = rows
    .flatMap(r => r.rewards.map(w => ({ ...w, email: r.email, name: r.name })))
    .sort((a, b) => b.day - a.day);
  const q = search.trim().toUpperCase();
  const shownCodes = q
    ? codes.filter(c => c.code.includes(q) || c.email.toUpperCase().includes(q))
    : codes;
  const ended = phaseAt(Date.now()) === 'ended';
  const knownEmails = rows.filter(r => r.uid).map(r => r.email);
  const typedEmail = bonusEmail.trim().toLowerCase();

  const toggleRedeemed = async c => {
    try {
      await db.collection(SPINS).doc(c.spinId).update({ redeemed: !c.redeemed });
      setSpins(list => list.map(s => (s.id === c.spinId ? { ...s, redeemed: !c.redeemed } : s)));
    } catch {
      setError('Кодът не се записа. Опитай пак.');
    }
  };

  const addBonus = async e => {
    e.preventDefault();
    if (!typedEmail) return;
    setAdding(true);
    setError(null);
    const data = {
      email: typedEmail,
      tickets: GAME.ticketsPerOrder,
      note: bonusNote.trim().slice(0, 100),
      at: firebase.firestore.FieldValue.serverTimestamp(),
    };
    try {
      const ref = await db.collection(BONUS).add(data);
      setBonuses(list => [...list, { ...data, id: ref.id, at: null }]);
      setBonusEmail('');
      setBonusNote('');
    } catch {
      setError('Билетите не се записаха. Провери имейла и опитай пак.');
    } finally {
      setAdding(false);
    }
  };

  const removeBonus = async b => {
    if (!window.confirm(`Да махна ли ${b.tickets} билета на ${b.email}?`)) return;
    try {
      await db.collection(BONUS).doc(b.id).delete();
      setBonuses(list => list.filter(x => x.id !== b.id));
    } catch {
      setError('Билетите не се изтриха. Опитай пак.');
    }
  };

  const draw = () => {
    const picked = pickWinner(rows, cryptoRandomBelow);
    return picked && {
      uid: picked.row.uid,
      email: picked.row.email,
      name: picked.row.name,
      tickets: picked.row.tickets,
      ticketNumber: picked.ticket + 1,
      totalTickets: picked.total,
    };
  };

  const runTrial = () => setTrial(draw());

  const runDraw = async () => {
    if (!window.confirm(`Тегленето е окончателно и не може да се повтори.\nУчастници: ${rows.length}, билети: ${totalTickets}.\nДа тегля ли?`)) return;
    const winner = draw();
    if (!winner) return;
    const result = {
      ...winner,
      prize: GAME.mainPrize,
      participants: rows.length,
      at: firebase.firestore.FieldValue.serverTimestamp(),
    };
    setSaving(true);
    try {
      await db.collection(DRAWS).doc(DRAW_DOC).set(result);
      setSaved({ ...result, at: null });
    } catch {
      // Ако вече има записано теглене (например от друг таб), показваме него.
      const snap = await db.collection(DRAWS).doc(DRAW_DOC).get().catch(() => null);
      if (snap?.exists) setSaved(snap.data());
      else setError('Тегленето не се записа. Опитай пак.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="xg-page xg-admin">
      <h1>Коледно колело – админ</h1>
      <GameStatus />
      <p>
        Завъртания {shortDate(START_DAY)} – {shortDate(END_DAY)}, теглене {shortDate(DRAW_DAY)}.{' '}
        <Link to={GAME.path}>Към страницата на играта</Link>
      </p>
      {error && <p className="xg-error" role="alert">{error}</p>}

      <div className="xg-stats xg-admin-stats">
        <div><strong>{rows.length}</strong><span>участници</span></div>
        <div><strong>{spins.length}</strong><span>завъртания</span></div>
        <div><strong>{bonuses.length}</strong><span>поръчки с билети</span></div>
        <div><strong>{rows.reduce((s, r) => s + r.invites, 0)}</strong><span>покани</span></div>
        <div><strong>{totalTickets}</strong><span>билета</span></div>
        <div><strong>{codes.filter(c => c.redeemed).length}</strong><span>използвани кода от {codes.length}</span></div>
      </div>

      <section className="xg-card">
        <h2>Кодове</h2>
        <p><small>Когато клиент каже код в чата: провери го тук, приложи печалбата и отбележи „Използван“.</small></p>
        <input
          className="xg-search"
          type="search"
          placeholder="Търси код или имейл"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="xg-table-wrap">
          <table className="xg-table">
            <thead>
              <tr><th>Код</th><th>Печалба</th><th>Участник</th><th>Важи до</th><th>Използван</th></tr>
            </thead>
            <tbody>
              {shownCodes.map(c => {
                const expired = today > c.validUntil;
                return (
                  <tr key={c.spinId} className={expired && !c.redeemed ? 'xg-used' : ''}>
                    <td><strong>{c.code}</strong></td>
                    <td>{c.title}<br /><small>{c.condition}</small></td>
                    <td>{c.name}<br /><small>{c.email}</small></td>
                    <td>{shortDate(c.validUntil)}{expired && !c.redeemed && <><br /><small>изтекъл</small></>}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={c.redeemed}
                        onChange={() => toggleRedeemed(c)}
                        aria-label={`Кодът ${c.code} е използван`}
                      />
                    </td>
                  </tr>
                );
              })}
              {shownCodes.length === 0 && <tr><td colSpan="5">Няма кодове.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="xg-card">
        <h2>Билети от поръчки</h2>
        <p><small>
          За всяка поръчка до {dateLabel(END_DAY)} добави {GAME.ticketsPerOrder} билета на имейла, който клиентът е казал в чата.
        </small></p>
        <form className="xg-bonus-form" onSubmit={addBonus}>
          <input
            type="email"
            required
            list="xg-emails"
            placeholder="Имейл от регистрацията"
            value={bonusEmail}
            onChange={e => setBonusEmail(e.target.value)}
          />
          <datalist id="xg-emails">
            {knownEmails.map(e => <option key={e} value={e} />)}
          </datalist>
          <input
            type="text"
            maxLength={100}
            placeholder="Бележка, напр. поръчка 12.11 – чаша"
            value={bonusNote}
            onChange={e => setBonusNote(e.target.value)}
          />
          <button type="submit" className="xg-btn" disabled={adding}>
            {adding ? 'Записва се…' : `+${GAME.ticketsPerOrder} билета`}
          </button>
        </form>
        {typedEmail && !knownEmails.includes(typedEmail) && (
          <p><small>Този имейл още не е въртял колелото. Билетите ще се броят, но провери дали е изписан правилно.</small></p>
        )}
        {bonuses.length > 0 && (
          <div className="xg-table-wrap">
            <table className="xg-table">
              <thead>
                <tr><th>Имейл</th><th>Бележка</th><th>Билети</th><th /></tr>
              </thead>
              <tbody>
                {bonuses.map(b => (
                  <tr key={b.id}>
                    <td>{b.email}</td>
                    <td>{b.note}</td>
                    <td>{b.tickets}</td>
                    <td>
                      <button type="button" className="xg-btn xg-btn-ghost xg-btn-small" onClick={() => removeBonus(b)}>
                        Махни
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="xg-card">
        <h2>Теглене</h2>
        {saved ? (
          <div className="xg-winner">
            <p>Победител за „{saved.prize}“:</p>
            <Winner winner={saved} />
            <p>Участници: {saved.participants}, билети: {saved.totalTickets}.</p>
            <p>Пиши на победителя и обяви във Facebook само първото име.</p>
          </div>
        ) : (
          <>
            <p className="xg-actions">
              <button type="button" className="xg-btn xg-btn-ghost" onClick={runTrial} disabled={!totalTickets}>
                Пробно теглене (не се записва)
              </button>
              <button type="button" className="xg-btn" onClick={runDraw} disabled={!ended || !totalTickets || saving}>
                {saving ? 'Записва се…' : 'Изтегли победителя'}
              </button>
            </p>
            {!ended && <p><small>Истинското теглене се отключва след края на завъртанията.</small></p>}
            {trial && (
              <div className="xg-trial">
                <p><strong>Пробно теглене</strong> – не е записано:</p>
                <Winner winner={trial} />
              </div>
            )}
          </>
        )}
      </section>

      <section className="xg-card">
        <h2>Участници</h2>
        <div className="xg-table-wrap">
          <table className="xg-table">
            <thead>
              <tr><th>Име</th><th>Имейл</th><th>Завъртания</th><th>Поръчки</th><th>Покани</th><th>Билети</th><th>Шанс</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.email}>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>{r.spins}</td>
                  <td>{r.orders}</td>
                  <td>{r.invites}</td>
                  <td>{r.tickets}</td>
                  <td>{((r.tickets / totalTickets) * 100).toFixed(1)}%</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan="7">Още няма участници.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
